import {
	OrderQuoteStatus,
	useAddOrderQuoteItemsMutation,
	useGetAllOrderQuoteQuery,
	useGetOrderQuoteItemByProductLazyQuery,
} from '@codezo/graphql';
import { Loader2, Plus, Search, X } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from './ui/dialog';
import { DatePicker } from './ui/date-picker';
import { Label } from './ui/label';
import { NumberInput } from './ui/number-input';
import { cn } from '../lib/utils';
import { AnimatedHeight } from './ui/animated-height';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { CreateOrderQuoteForm, type CreatedQuote } from './quotes/modals/CreateOrderQuoteForm';
import { ProductOnQuoteNotice } from './quotes/ProductOnQuoteNotice';

interface AddToQuoteModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	productIds: string[];
	/** Product names keyed by ID, for display in the quantities step */
	productNames?: Record<string, string>;
	/** Owning organization id per product (null = admin product). Scopes the target quote list. */
	productOrganizations?: Record<string, string | null>;
	/** Owning organization name per product, for display when locking a new quote's client. */
	productOrganizationNames?: Record<string, string>;
	onSuccess: () => void;
}

export function AddToQuoteModal({
	open,
	onOpenChange,
	productIds,
	productNames,
	productOrganizations,
	productOrganizationNames,
	onSuccess,
}: AddToQuoteModalProps) {
	const [step, setStep] = useState<'quote' | 'create' | 'quantities'>('quote');
	const [selectedQuote, setSelectedQuote] = useState('');
	const [selectedQuoteName, setSelectedQuoteName] = useState('');
	const [query, setQuery] = useState('');
	const debouncedQuery = useDebouncedValue(query);
	const [page, setPage] = useState(1);

	// Quantities: { productId: [500, 1000, 2000] }
	const [quantities, setQuantities] = useState<Record<string, number[]>>({});
	const [deadlines, setDeadlines] = useState<Record<string, string | null>>({});

	const [addItems, { loading: adding }] = useAddOrderQuoteItemsMutation();
	const [fetchExistingItem] = useGetOrderQuoteItemByProductLazyQuery();
	const [loadingExisting, setLoadingExisting] = useState(false);

	// Prefill data (existing quantities/deadlines for these products) per quote.
	// Prefetched when a quote is picked so advancing to the quantities step is
	// instant — no loader flash on Next for what's usually a sub-second fetch.
	type Prefill = {
		/** Products already on this quote. `addOrderQuoteItems` refuses these. */
		onQuote: Record<string, boolean>;
		/** Their existing tiers, shown as context for why they are skipped. */
		quantities: Record<string, number[]>;
	};
	const [prefillByQuote, setPrefillByQuote] = useState<Record<string, Prefill>>({});

	// Org scoping: a product that belongs to an organization can only be added to
	// that organization's quotes. Admin products (null org) can go on any quote.
	const selectedOrgIds = [
		...new Set(
			productIds
				.map((pid) => productOrganizations?.[pid])
				.filter((orgId): orgId is string => !!orgId)
		),
	];
	const crossOrg = selectedOrgIds.length > 1;
	const requiredOrgId = selectedOrgIds.length === 1 ? selectedOrgIds[0] : undefined;
	const requiredOrgName = requiredOrgId
		? productIds
				.map((pid) =>
					productOrganizations?.[pid] === requiredOrgId
						? productOrganizationNames?.[pid]
						: undefined
				)
				.find(Boolean)
		: undefined;

	const { data, loading } = useGetAllOrderQuoteQuery({
		variables: {
			input: {
				pagination: { limit: 20, currentPage: page },
				search: debouncedQuery || undefined,
				statuses: [OrderQuoteStatus.Draft],
				organizationIds: requiredOrgId ? [requiredOrgId] : undefined,
			},
		},
		fetchPolicy: 'no-cache',
		skip: !open || step !== 'quote' || crossOrg,
	});

	const scroll = useInfiniteScroll({
		items: data?.getAllOrderQuote?.data,
		pageInfo: data?.getAllOrderQuote?.pageInfo,
		loading,
		resetKey: debouncedQuery,
	});

	if (scroll.page !== page) setPage(scroll.page);

	const reset = () => {
		setStep('quote');
		setSelectedQuote('');
		setSelectedQuoteName('');
		setQuery('');
		setQuantities({});
		setDeadlines({});
		setPrefillByQuote({});
		scroll.reset();
		setPage(1);
	};

	const handleOpenChange = (next: boolean) => {
		if (!next) reset();
		onOpenChange(next);
	};

	// Find which of these products are already on the selected quote. They cannot
	// be added again — the mutation refuses the whole batch over one of them — so
	// the step skips them rather than offering fields it cannot honour.
	const fetchPrefill = useCallback(
		async (quoteId: string): Promise<Prefill> => {
			const onQuote: Record<string, boolean> = {};
			const quantities: Record<string, number[]> = {};
			await Promise.all(
				productIds.map(async (productId) => {
					try {
						const { data } = await fetchExistingItem({
							variables: { orderQuoteId: quoteId, productId },
							fetchPolicy: 'no-cache',
						});
						const existingItem = data?.getOrderQuoteItemByProduct;
						onQuote[productId] = !!existingItem;
						quantities[productId] =
							existingItem?.quantities?.map((q) => q.quantity) ?? [];
					} catch {
						// Treat an unreadable item as absent: the mutation is the
						// authority and will refuse it if it is really there.
						onQuote[productId] = false;
						quantities[productId] = [];
					}
				})
			);
			return { onQuote, quantities };
		},
		[productIds, fetchExistingItem]
	);

	// Prefetch prefill data as soon as a quote is picked — usually done before the
	// user reaches for Next, making the step change instant.
	useEffect(() => {
		if (!selectedQuote || prefillByQuote[selectedQuote]) return;
		let cancelled = false;
		fetchPrefill(selectedQuote).then((prefill) => {
			if (!cancelled) setPrefillByQuote((prev) => ({ ...prev, [selectedQuote]: prefill }));
		});
		return () => {
			cancelled = true;
		};
	}, [selectedQuote, prefillByQuote, fetchPrefill]);

	const handleSelectQuote = async () => {
		if (!selectedQuote) return;
		// Use the prefetched prefill for an instant transition; only block (and
		// show the loader) if a very fast click beat the prefetch.
		let prefill = prefillByQuote[selectedQuote];
		if (!prefill) {
			setLoadingExisting(true);
			prefill = await fetchPrefill(selectedQuote);
			setLoadingExisting(false);
		}
		setQuantities(
			Object.fromEntries(
				productIds.filter((id) => !prefill.onQuote[id]).map((id) => [id, [0]])
			)
		);
		setDeadlines(Object.fromEntries(productIds.map((id) => [id, null])));
		setStep('quantities');
	};

	// A freshly created quote has no existing items — go straight to quantities
	// with empty defaults.
	const handleQuoteCreated = (quote: CreatedQuote) => {
		setSelectedQuote(quote.id);
		setSelectedQuoteName(quote.name);
		setQuantities(Object.fromEntries(productIds.map((id) => [id, [0]])));
		setDeadlines(Object.fromEntries(productIds.map((id) => [id, null])));
		setStep('quantities');
	};

	// Products already on the chosen quote are skipped; the rest are what we send.
	const selectedPrefill = prefillByQuote[selectedQuote];
	const blockedIds = productIds.filter((id) => selectedPrefill?.onQuote[id]);
	const addableIds = productIds.filter((id) => !selectedPrefill?.onQuote[id]);
	const allBlocked = !!selectedPrefill && addableIds.length === 0;

	const addQuantity = (productId: string) => {
		setQuantities((prev) => ({
			...prev,
			[productId]: [...(prev[productId] || []), 0],
		}));
	};

	const updateQuantity = (productId: string, index: number, value: number) => {
		setQuantities((prev) => ({
			...prev,
			[productId]: (prev[productId] || []).map((q, i) => (i === index ? value : q)),
		}));
	};

	const removeQuantity = (productId: string, index: number) => {
		setQuantities((prev) => ({
			...prev,
			[productId]: (prev[productId] || []).filter((_, i) => i !== index),
		}));
	};

	const handleAdd = async () => {
		try {
			// One product already on the quote would refuse the whole batch.
			const items = addableIds.map((productId) => ({
				productId,
				quantities: (quantities[productId] || []).filter((q) => q > 0),
				deadline: deadlines[productId] ?? undefined,
			}));

			const { data: res } = await addItems({
				variables: {
					input: {
						orderQuoteId: selectedQuote,
						items,
					},
				},
			});

			if (!res?.addOrderQuoteItems?.success) {
				toast.error(res?.addOrderQuoteItems?.message || 'Failed to add products to quote');
				return;
			}

			toast.success(
				`${items.length} product${items.length > 1 ? 's' : ''} added to ${selectedQuoteName}`
			);
			onSuccess();
			handleOpenChange(false);
		} catch {
			toast.error('Failed to add products to quote');
		}
	};

	const hasValidQuantities = addableIds.some((id) => (quantities[id] || []).some((q) => q > 0));

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className={cn('sm:max-w-md', step === 'create' && 'sm:max-w-lg')}>
				<AnimatedHeight step={`${step}-${crossOrg}`}>
					<DialogHeader>
						<DialogTitle>
							{step === 'quote'
								? 'Add to quote'
								: step === 'create'
									? 'New quote'
									: 'Set quantities'}
						</DialogTitle>
						<DialogDescription>
							{step === 'quote'
								? `Select a quote for ${productIds.length} product${productIds.length > 1 ? 's' : ''}.`
								: step === 'create'
									? 'Create a new quote and add the selected products to it.'
									: `Set quantities for ${selectedQuoteName}.`}
						</DialogDescription>
					</DialogHeader>

					{step === 'create' ? (
						<CreateOrderQuoteForm
							open={open}
							lockedOrganizationId={requiredOrgId}
							lockedOrganizationName={requiredOrgName}
							submitLabel="Create & continue"
							cancelLabel="Back"
							onCancel={() => setStep('quote')}
							onCreated={handleQuoteCreated}
						/>
					) : step === 'quote' ? (
						<>
							<DialogBody className="flex flex-col gap-0 p-0">
								{crossOrg ? (
									<div className="flex h-[300px] flex-col items-center justify-center gap-2 px-8 text-center">
										<div className="text-base font-medium text-text-1">
											Products from different organizations
										</div>
										<div className="text-sm leading-relaxed text-text-3">
											The selected products belong to different organizations,
											so they can&apos;t be added to a single quote. Select
											products from one organization — internal products can
											be combined with any.
										</div>
									</div>
								) : (
									<>
										<div className="relative shrink-0 px-4 pt-4 pb-2">
											<Search
												size={14}
												className="pointer-events-none absolute left-7 top-1/2 -translate-y-1/2 text-text-4"
											/>
											<input
												value={query}
												onChange={(e) => setQuery(e.target.value)}
												placeholder="Search quotes…"
												className="h-8 w-full rounded-[var(--r-2)] border border-line-2 bg-surface-input pl-9 pr-3 text-base text-text-1 outline-none placeholder:text-text-4 focus:border-line-3"
												autoFocus
											/>
										</div>

										<div className="shrink-0 px-4 pb-2">
											<button
												type="button"
												onClick={() => setStep('create')}
												className="flex w-fit cursor-pointer items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
											>
												<Plus size={15} className="text-text-4" />
												Create new quote
											</button>
										</div>

										{requiredOrgId && (
											<div className="shrink-0 px-4 pb-1 text-xs text-text-4">
												Only draft quotes for the selected products’
												organization are shown.
											</div>
										)}

										<div className="scroll-thin h-[300px] overflow-y-auto px-4 py-1">
											<div className="flex flex-col gap-0.5">
												{scroll.items.map((quote) => {
													const isSelected = selectedQuote === quote.id;
													return (
														<button
															key={quote.id}
															onClick={() => {
																setSelectedQuote(quote.id);
																setSelectedQuoteName(quote.name);
															}}
															className={cn(
																'flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-surface-hover',
																isSelected && 'bg-surface-fill'
															)}
														>
															<Checkbox
																checked={isSelected}
																tabIndex={-1}
																className="pointer-events-none"
															/>
															<div className="min-w-0 flex-1">
																<div className="truncate text-base font-medium text-text-1">
																	{quote.name}
																</div>
																{quote.organization?.name && (
																	<div className="truncate text-xs text-text-4">
																		{quote.organization.name}
																	</div>
																)}
															</div>
														</button>
													);
												})}
												{scroll.initialLoading && (
													<div className="flex items-center justify-center py-6 text-sm text-text-4">
														Loading…
													</div>
												)}
												{!loading &&
													scroll.items.length === 0 &&
													!scroll.initialLoading && (
														<div className="flex items-center justify-center py-6 text-center text-sm text-text-4">
															{query
																? 'No quotes match your search.'
																: 'No quotes available.'}
														</div>
													)}
												{scroll.loadingMore && (
													<div className="py-2 text-center text-xs text-text-4">
														Loading more…
													</div>
												)}
												<div ref={scroll.sentinelRef} className="h-1" />
											</div>
										</div>
										{/* Say it here, where the quote is chosen, rather than
										    letting them fill in fields we cannot submit. */}
										{blockedIds.length > 0 && (
											<div className="shrink-0 px-4 pb-3">
												<div className="rounded-lg border border-line-1 bg-surface-sunken px-2.5 py-2 text-2xs leading-relaxed text-text-3">
													{allBlocked
														? `${blockedIds.length === 1 ? 'This product is' : 'All these products are'} already on this quote. Change quantities from the quote itself.`
														: `${blockedIds.length} of ${productIds.length} products are already on this quote and will be skipped.`}
												</div>
											</div>
										)}
									</>
								)}
							</DialogBody>

							<DialogFooter>
								<Button variant="outline" onClick={() => handleOpenChange(false)}>
									Cancel
								</Button>
								<Button
									onClick={handleSelectQuote}
									disabled={
										!selectedQuote || loadingExisting || crossOrg || allBlocked
									}
								>
									{loadingExisting ? (
										<>
											<Loader2 size={14} className="animate-spin" /> Loading…
										</>
									) : (
										'Next'
									)}
								</Button>
							</DialogFooter>
						</>
					) : (
						<>
							<DialogBody>
								<div className="flex flex-col gap-4">
									{productIds.map((productId) => (
										<div key={productId} className="flex flex-col gap-2">
											<div className="text-base font-medium text-text-1">
												{productNames?.[productId] || productId}
											</div>
											{selectedPrefill?.onQuote[productId] ? (
												<ProductOnQuoteNotice
													quantities={
														selectedPrefill.quantities[productId] ?? []
													}
												/>
											) : (
												<>
													<div className="flex flex-col gap-1.5">
														{(quantities[productId] || []).map(
															(qty, i) => (
																<div
																	key={i}
																	className="flex items-center gap-1.5"
																>
																	<NumberInput
																		className="flex-1"
																		min={1}
																		value={qty || ''}
																		onChange={(e) =>
																			updateQuantity(
																				productId,
																				i,
																				parseInt(
																					(
																						e.target as HTMLInputElement
																					).value
																				) || 0
																			)
																		}
																		placeholder="Quantity"
																	/>
																	{(quantities[productId] || [])
																		.length > 1 && (
																		<button
																			type="button"
																			onClick={() =>
																				removeQuantity(
																					productId,
																					i
																				)
																			}
																			className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-text-4 transition-colors hover:bg-surface-hover hover:text-text-2"
																		>
																			<X size={14} />
																		</button>
																	)}
																</div>
															)
														)}
													</div>
													<button
														type="button"
														onClick={() => addQuantity(productId)}
														disabled={
															(quantities[productId] || []).length >=
															3
														}
														className="flex items-center gap-1 self-start bg-transparent text-sm text-text-3 transition-colors enabled:hover:text-text-1 disabled:cursor-not-allowed disabled:opacity-40"
													>
														<Plus size={12} />
														{(quantities[productId] || []).length >= 3
															? 'Max 3 quantities'
															: 'Add quantity'}
													</button>
													<div className="mt-1 flex flex-col gap-1">
														<Label>Deadline</Label>
														<DatePicker
															value={
																deadlines[productId]
																	? new Date(
																			deadlines[productId]!
																		)
																	: null
															}
															onChange={(d: Date | undefined) =>
																setDeadlines((prev) => ({
																	...prev,
																	[productId]:
																		d?.toISOString() ?? null,
																}))
															}
															placeholder="No deadline"
															minDate={new Date()}
														/>
													</div>
												</>
											)}
										</div>
									))}
								</div>
							</DialogBody>

							<DialogFooter>
								<Button variant="outline" onClick={() => setStep('quote')}>
									Back
								</Button>
								<Button
									onClick={handleAdd}
									disabled={!hasValidQuantities || adding}
								>
									{adding ? 'Adding…' : 'Add to quote'}
								</Button>
							</DialogFooter>
						</>
					)}
				</AnimatedHeight>
			</DialogContent>
		</Dialog>
	);
}
